import React from 'react';
import { AppPage } from '../types';
import {
  FileText,
  NotebookPen,
  MessageCircle,
  ClipboardCheck,
  Brain,
  BookOpen,
  MapPin,
  HeartHandshake,
  Target,
  Sparkles,
  Heart,
  Moon,
  Apple,
  Droplet,
  Activity,
  Users,
  Headphones,
  Wind,
  CheckCircle2,
  TrendingUp
} from 'lucide-react';

export const PERSONALIZED_ONBOARDING_STORAGE_KEY = 'eir-personalized-onboarding-completed';
export const ONBOARDING_ANSWERS_STORAGE_KEY = 'eir-onboarding-answers';
export const ONBOARDING_RECOMMENDATIONS_STORAGE_KEY = 'eir-onboarding-recommendations';

export interface OnboardingAnswer {
  primaryGoal?: string | string[]; // Can be single or multiple
  currentSituation?: string | string[]; // Can be single or multiple
  immediateNeeds?: string[];
}

export interface FeatureRecommendation {
  id: string;
  page: AppPage;
  subPage?: string; // For navigating to specific tabs within a page
  title: string;
  description: string;
  icon: React.ReactNode;
  priority: number;
  category: string;
}

// Feature definitions mapped to actual sidebar navigation (excluding beta features)
const FEATURE_DEFINITIONS: Omit<FeatureRecommendation, 'priority'>[] = [
  // Health Journey (Track Your Health)
  {
    id: 'ehr',
    page: 'ehr',
    title: 'Health Journey',
    description: 'View your complete health timeline with clinical records and personal notes',
    icon: <NotebookPen size={24} />,
    category: 'Track Your Health'
  },
  {
    id: 'journal',
    page: 'ehr',
    subPage: 'journal',
    title: 'Health Journal',
    description: 'Keep personal health notes and track your wellbeing over time',
    icon: <FileText size={24} />,
    category: 'Track Your Health'
  },
  {
    id: 'eir-chat',
    page: 'eir-chat',
    title: 'AI Chat Assistant',
    description: 'Chat with an AI assistant about your health data and get explanations',
    icon: <MessageCircle size={24} />,
    category: 'Track Your Health'
  },
  
  // Know Your Health
  {
    id: 'health-assessment',
    page: 'health-assessment',
    title: 'Health Assessments',
    description: 'Take structured assessments to understand your physical and mental wellbeing',
    icon: <ClipboardCheck size={24} />,
    category: 'Know Your Health'
  },
  
  // Health Programs
  {
    id: 'health-programs',
    page: 'health-programs',
    title: 'Health Programs',
    description: 'Follow evidence-based CBT programs for ADHD, anxiety, depression and more',
    icon: <Brain size={24} />,
    category: 'Improve Your Health'
  },
  
  // Personal Growth - Self Awareness
  {
    id: 'feedback',
    page: 'personal-growth',
    subPage: 'feedback',
    title: 'Feedback Studio',
    description: 'Discover how others see you through meaningful feedback from friends and family',
    icon: <Heart size={24} />,
    category: 'Self-Awareness'
  },
  {
    id: 'emotions',
    page: 'personal-growth',
    subPage: 'emotions',
    title: 'Understanding Emotions',
    description: 'Explore the emotion wheel and understand what your feelings are telling you',
    icon: <Heart size={24} />,
    category: 'Self-Awareness'
  },
  {
    id: 'shadow-work',
    page: 'personal-growth',
    subPage: 'room-for-improvement',
    title: 'Shadow Work',
    description: 'Journey into your shadow with curiosity and compassion for personal growth',
    icon: <Moon size={24} />,
    category: 'Self-Awareness'
  },
  {
    id: 'relationships',
    page: 'personal-growth',
    subPage: 'relationships',
    title: 'Understanding Relationships',
    description: 'Discover your attachment style, love language, and build deeper connections',
    icon: <Users size={24} />,
    category: 'Self-Awareness'
  },
  {
    id: 'thinking',
    page: 'personal-growth',
    subPage: 'thinking',
    title: 'The Art of Thinking',
    description: 'Recognize cognitive biases and thought traps for clearer decision-making',
    icon: <Brain size={24} />,
    category: 'Self-Awareness'
  },
  
  // Personal Growth - Physical Health
  {
    id: 'sleep',
    page: 'personal-growth',
    subPage: 'sleep',
    title: 'Sleep Upgrade',
    description: 'Learn evidence-based strategies for deep, restorative sleep',
    icon: <Moon size={24} />,
    category: 'Physical Health'
  },
  {
    id: 'food',
    page: 'personal-growth',
    subPage: 'food',
    title: 'Food & Nutrition',
    description: 'Build a compassionate, evidence-based relationship with food',
    icon: <Apple size={24} />,
    category: 'Physical Health'
  },
  {
    id: 'water',
    page: 'personal-growth',
    subPage: 'water',
    title: 'Water & Drinks',
    description: 'Understand hydration and how different beverages affect your health',
    icon: <Droplet size={24} />,
    category: 'Physical Health'
  },
  {
    id: 'movement',
    page: 'personal-growth',
    subPage: 'movement',
    title: 'Movement & Activity',
    description: 'Build a sustainable movement practice with evidence-based guidelines',
    icon: <Activity size={24} />,
    category: 'Physical Health'
  },
  
  // Personal Growth - Daily Practices
  {
    id: 'habit-tracker',
    page: 'personal-growth',
    subPage: 'habit-tracker',
    title: 'Habit Tracker',
    description: 'Build lasting habits through consistent daily practice and track streaks',
    icon: <CheckCircle2 size={24} />,
    category: 'Daily Practices'
  },
  {
    id: 'health-actions',
    page: 'personal-growth',
    subPage: 'health-actions',
    title: 'Health Actions',
    description: 'Brief, actionable steps that take 15 minutes or less to improve your health',
    icon: <Target size={24} />,
    category: 'Daily Practices'
  },
  {
    id: 'writing',
    page: 'personal-growth',
    subPage: 'writing',
    title: 'Therapeutic Writing',
    description: 'Process emotions and express gratitude through guided writing prompts',
    icon: <FileText size={24} />,
    category: 'Daily Practices'
  },
  
  // Personal Growth - Wellness
  {
    id: 'listen',
    page: 'personal-growth',
    subPage: 'listen',
    title: 'Listen & Relax',
    description: 'Therapeutic soundscapes, guided meditations, and ASMR for calm',
    icon: <Headphones size={24} />,
    category: 'Wellness'
  },
  {
    id: 'breathe',
    page: 'personal-growth',
    subPage: 'breathe',
    title: 'Breathing Exercises',
    description: 'Calm your nervous system with 4-7-8, box breathing, and more',
    icon: <Wind size={24} />,
    category: 'Wellness'
  },
  
  // Get Care
  {
    id: 'get-care',
    page: 'get-care',
    title: 'Get Care',
    description: 'Find healthcare facilities and navigate the Swedish healthcare system',
    icon: <HeartHandshake size={24} />,
    category: 'Get Care'
  },
  {
    id: 'health-map',
    page: 'health-map',
    title: 'Health Map',
    description: 'Find healthcare facilities near you across Sweden',
    icon: <MapPin size={24} />,
    category: 'Get Care'
  },
  {
    id: 'request-care',
    page: 'request-care',
    title: 'Request Care',
    description: 'Generate professional care request documents for healthcare providers',
    icon: <Target size={24} />,
    category: 'Get Care'
  },
  
  // Eir Docs
  {
    id: 'eir-docs',
    page: 'eir-docs',
    title: 'Eir Docs',
    description: 'Tutorials, documentation, and guides to make the most of EIR Space',
    icon: <BookOpen size={24} />,
    category: 'Learn'
  },
  
  // For You
  {
    id: 'for-you',
    page: 'for-you',
    title: 'For You',
    description: 'Your personalized dashboard with recommendations based on your goals',
    icon: <Sparkles size={24} />,
    category: 'Personalized'
  },
  
  // Personal Growth overview
  {
    id: 'personal-growth',
    page: 'personal-growth',
    title: 'Improve Your Health',
    description: 'Comprehensive tools for self-awareness, physical health, and daily wellness',
    icon: <TrendingUp size={24} />,
    category: 'Improve Your Health'
  }
];

// Answer to feature mapping - maps user answers to relevant features
const ANSWER_FEATURE_MAP: Record<string, string[]> = {
  // Primary Goal mappings
  'understand-records': ['ehr', 'eir-chat', 'journal'],
  'manage-condition': ['health-programs', 'health-assessment', 'health-actions', 'habit-tracker'],
  'improve-wellbeing': ['personal-growth', 'health-assessment', 'sleep', 'movement', 'habit-tracker'],
  'prepare-visits': ['request-care', 'get-care', 'ehr', 'eir-chat'],
  'track-health': ['journal', 'habit-tracker', 'health-assessment', 'for-you'],
  
  // Current Situation mappings
  'has-records': ['ehr', 'eir-chat', 'journal'],
  'chronic-condition': ['health-programs', 'health-actions', 'habit-tracker', 'writing'],
  'learn-topics': ['eir-docs', 'thinking', 'emotions'],
  'new-tracking': ['journal', 'health-assessment', 'habit-tracker', 'for-you'],
  'navigate-care': ['get-care', 'health-map', 'request-care'],
  
  // Immediate Needs mappings
  'get-data': ['ehr', 'eir-chat'],
  'take-assessment': ['health-assessment'],
  'find-program': ['health-programs'],
  'relax-now': ['listen', 'breathe'],
  'track-symptoms': ['journal', 'habit-tracker'],
  'plan-care': ['request-care', 'get-care'],
  'understand-self': ['feedback', 'emotions', 'shadow-work', 'relationships'],
  'improve-sleep': ['sleep'],
  'improve-fitness': ['movement', 'habit-tracker'],
  'eat-better': ['food', 'water']
};

/**
 * Generate a pre-made onboarding welcome message based on recommendations
 * This avoids an LLM call and provides instant feedback
 */
export function generateOnboardingMessage(
  recommendations: FeatureRecommendation[],
  answers: OnboardingAnswer
): string {
  // Get top 5 recommendations
  const topRecs = getTopRecommendations(recommendations, 5);
  
  // Create feature references
  const featureRefs = topRecs.map(r => {
    if (r.id === 'journal' || (r.page === 'ehr' && r.subPage === 'journal')) {
      return `[feature:ehr:journal]`;
    }
    if (r.subPage) {
      return `[feature:${r.page}:${r.subPage}]`;
    }
    return `[feature:${r.page}]`;
  });
  
  // Get primary goals for context
  const goals = Array.isArray(answers.primaryGoal) ? answers.primaryGoal : (answers.primaryGoal ? [answers.primaryGoal] : []);
  const goalText = goals.length > 0 
    ? goals.map(g => {
        const goalNames: Record<string, string> = {
          'understand-records': 'förstå dina journaler',
          'improve-wellbeing': 'förbättra ditt välmående',
          'manage-condition': 'hantera en hälsosituation',
          'track-progress': 'spåra din utveckling',
          'get-care': 'få vård'
        };
        return goalNames[g] || g;
      }).join(', ')
    : 'förbättra din hälsa';
  
  // Build concise feature descriptions with references
  const featureList = topRecs.map((rec, index) => {
    const ref = featureRefs[index];
    return `${ref} - ${rec.description}`;
  }).join('\n\n');
  
  // Build first steps (use available features)
  const firstSteps = [];
  if (topRecs[0]) firstSteps.push(`1. Börja med ${featureRefs[0]} för att komma igång direkt`);
  if (topRecs[1]) firstSteps.push(`2. Utforska ${featureRefs[1]} för att få en bättre bild av din situation`);
  if (topRecs[2]) firstSteps.push(`3. Använd ${featureRefs[2]} för att bygga goda vanor`);
  
  return `Hej! Vad roligt att du är igång. Här är en kort guide till de funktioner som passar dina mål (${goalText}):

${featureList}

**Första steg:**
${firstSteps.join('\n')}

Alla funktioner är klickbara – klicka på dem för att navigera direkt. Behöver du mer hjälp? Ställ gärna en fråga här i chatten!`;
}

/**
 * Calculate feature recommendations based on onboarding answers
 */

export function calculateRecommendations(answers: OnboardingAnswer): FeatureRecommendation[] {
  const featureScores: Record<string, number> = {};
  
  // Initialize all features with 0 score
  FEATURE_DEFINITIONS.forEach(feature => {
    featureScores[feature.id] = 0;
  });
  
  // Score based on primary goal (highest weight) - handle both single and multiple
  if (answers.primaryGoal) {
    const goals = Array.isArray(answers.primaryGoal) ? answers.primaryGoal : [answers.primaryGoal];
    goals.forEach((goal, goalIndex) => {
      const goalFeatures = ANSWER_FEATURE_MAP[goal] || [];
      goalFeatures.forEach((featureId, index) => {
        // Give higher priority to first items in the list, and first goals selected
        const weight = (4 - Math.min(index, 2)) * (goals.length > 1 ? 1.2 : 1); // Boost if multiple goals
        featureScores[featureId] = (featureScores[featureId] || 0) + weight;
      });
    });
  }
  
  // Score based on current situation (medium weight) - handle both single and multiple
  if (answers.currentSituation) {
    const situations = Array.isArray(answers.currentSituation) ? answers.currentSituation : [answers.currentSituation];
    situations.forEach((situation, situationIndex) => {
      const situationFeatures = ANSWER_FEATURE_MAP[situation] || [];
      situationFeatures.forEach((featureId, index) => {
        // Give higher priority to first items in the list, and first situations selected
        const weight = (3 - Math.min(index, 2)) * (situations.length > 1 ? 1.1 : 1); // Slight boost if multiple situations
        featureScores[featureId] = (featureScores[featureId] || 0) + weight;
      });
    });
  }
  
  // Score based on immediate needs (additive for multiple selections)
  if (answers.immediateNeeds && answers.immediateNeeds.length > 0) {
    answers.immediateNeeds.forEach(need => {
      const needFeatures = ANSWER_FEATURE_MAP[need] || [];
      needFeatures.forEach((featureId, index) => {
        featureScores[featureId] = (featureScores[featureId] || 0) + (2 - Math.min(index, 1));
      });
    });
  }
  
  // Convert scores to recommendations
  const recommendations: FeatureRecommendation[] = FEATURE_DEFINITIONS.map(feature => ({
    ...feature,
    priority: featureScores[feature.id] || 0
  }));
  
  // Sort by priority (highest first), then alphabetically
  recommendations.sort((a, b) => {
    if (b.priority !== a.priority) {
      return b.priority - a.priority;
    }
    return a.title.localeCompare(b.title);
  });
  
  return recommendations;
}

/**
 * Get top N recommendations sorted by priority (highest first)
 */
export function getTopRecommendations(
  recommendations: FeatureRecommendation[],
  count: number = 5
): FeatureRecommendation[] {
  return recommendations
    .filter(r => r.priority > 0)
    .sort((a, b) => b.priority - a.priority) // Sort by priority descending
    .slice(0, count);
}

/**
 * Get recommendations by category
 */
export function getRecommendationsByCategory(
  recommendations: FeatureRecommendation[]
): Record<string, FeatureRecommendation[]> {
  const byCategory: Record<string, FeatureRecommendation[]> = {};
  
  recommendations.forEach(rec => {
    if (!byCategory[rec.category]) {
      byCategory[rec.category] = [];
    }
    byCategory[rec.category].push(rec);
  });
  
  return byCategory;
}
